/**
 * Ошибка сохранения заявок игроков (`upsertChampionshipApplication` и
 * `upsertChampionshipApplicationUser`) с кодом из `extensions.code` GraphQL-ошибки.
 *
 * Код нужен, чтобы отличить отказ, у которого есть готовый текст для пользователя
 * (7016), от остальных: их сообщения технические, и показывать их нельзя.
 */

/**
 * `USER_ALREADY_ACCEPTED_IN_ANOTHER_TEAM` (IMGM-1524 бэкенда): игрока подтверждают (`ACCEPTED`),
 * а он уже подтверждён в другой команде этого же соревнования. Ничего не сохраняется —
 * пакетный запрос откатывается целиком. В `message` — готовый русский текст с ФИО игрока
 * и названием команды; конфликт в нём всегда один, даже если конфликтных игроков несколько.
 */
export const PLAYER_ACCEPTED_IN_ANOTHER_TEAM_CODE = 7016;

/** Отказ сохранения в виде, пригодном для `rejectWithValue`: простой объект, без `Error`. */
export interface ApplicationSaveFailure {
  message: string;
  code?: number;
}

/** Бросает репозиторий: обычный `new Error(message)` потерял бы код ошибки. */
export class ApplicationSaveError extends Error {
  constructor(
    message: string,
    readonly code?: number,
  ) {
    super(message);
    this.name = 'ApplicationSaveError';
  }
}

/** Код приходит числом, но строку тоже принимаем — так же, как соседние разборщики кодов. */
export function readErrorCode(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const code = Number(value);
    return Number.isFinite(code) ? code : undefined;
  }
  return undefined;
}

/** Любая пойманная в thunk-е ошибка → сериализуемый `ApplicationSaveFailure`. */
export function toApplicationSaveFailure(error: unknown): ApplicationSaveFailure {
  if (error instanceof ApplicationSaveError) return { message: error.message, code: error.code };
  if (error instanceof Error) return { message: error.message };
  return { message: String(error) };
}

function isFailure(value: unknown): value is ApplicationSaveFailure {
  return !!value && typeof value === 'object' && typeof (value as ApplicationSaveFailure).message === 'string';
}

/** Текст отказа для проверок вроде `limit exceeded`; у отказа без сообщения — пустая строка. */
export function getApplicationSaveErrorText(error: unknown): string {
  if (isFailure(error)) return error.message;
  return typeof error === 'string' ? error : '';
}

/**
 * Что показать пользователю: при коде 7016 — текст бэкенда как есть (он называет игрока
 * и команду, в которой тот уже заявлен), иначе — `fallback` экрана. Определяем по коду,
 * а не по тексту: формулировку бэкенд может поменять.
 */
export function getApplicationSaveErrorMessage(error: unknown, fallback: string): string {
  if (isFailure(error) && error.code === PLAYER_ACCEPTED_IN_ANOTHER_TEAM_CODE && error.message.trim() !== '') {
    return error.message;
  }
  return fallback;
}
